import { API, APP_URLS, clientCentralApiStrawberry, clientNeoAuth } from "@neonumy/api-clients";
import { Header, validateId } from "@neonumy/neonumy-ui";
import * as Sentry from "@sentry/browser";
import { Layout } from "antd";
import axios from "axios";
import { routes } from "config/routes";
import { useFieldsSelection } from "hooks/useFieldsSelection";
import { usePermissions } from "hooks/usePermissions";
import Cookies from "js-cookie";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import Logo from "resources/images/logo.svg?react";
import useAuthStore from "views/hooks/useAuthStore";
import useCoreStore from "views/hooks/useCoreStore";

interface ApplicationWrapperProps {
  children: React.ReactNode;
}

function ApplicationWrapper(props: ApplicationWrapperProps): React.ReactElement {
  const routeParams: RouteParams = useParams();
  const { canUpdateCompany } = usePermissions();

  const resetStore = useAuthStore(state => state.resetStore);
  const setAccountingFirmSelected = useCoreStore(state => state.setAccountingFirmSelected);

  const companySelected = useCoreStore(state => state.companySelected);
  const accountingFirmSelected = useCoreStore(state => state.accountingFirmSelected);
  const user = useAuthStore(state => state.userData);
  const userIsClient = ["client"].includes(user?.userCategory?.slug ?? "");

  const companyId = routeParams?.companyId;
  const accountingFirmId = routeParams?.accountingFirmId;

  const { getCompany, getAccountingFirm } = useFieldsSelection();

  useEffect(() => {
    // No user means either not authenticated yet or a logout in progress.
    // `PrivateRoute` owns that flow; navigating here only adds redirects.
    if (!user) return;

    const { companies, accountingFirms } = user;
    const companyID = companyId || (companies && companies.length === 1 && companies[0]);
    const accountingFirmID =
      accountingFirmId || (accountingFirms && accountingFirms.length === 1 && accountingFirms[0]);

    if (companyID && validateId(companyID) && companySelected?.id !== companyID) {
      getCompany({
        variables: {
          id: companyID,
        },
      });
      if (!companyId) {
        routes.navigate(`/company/${companyID}/dashboard`);
      }
    } else if (accountingFirmID && validateId(accountingFirmID) && !companyID) {
      getAccountingFirm({
        variables: {
          accountingFirmId: accountingFirmID,
        },
      });
    }
  }, [companyId, accountingFirmId, user]);

  const handleLogout = async () => {
    const accessToken = Cookies.get("access_token");

    // Stop active queries before revoking. `resetStore` would refetch them all
    // with the revoked token, and every 401 sends the app to the login URL.
    clientNeoAuth.stop();
    clientCentralApiStrawberry.stop();

    try {
      await axios(API.revokeToken, {
        method: "post",
        data: `token=${accessToken}&client_id=${API.clientId}`,
      });
    } finally {
      await Promise.all([
        clientNeoAuth.clearStore().catch(() => undefined),
        clientCentralApiStrawberry.clearStore().catch(() => undefined),
      ]);
      resetStore();
      Sentry.setUser(null);

      Cookies.remove("access_token");
      Cookies.remove("expireDate");

      // Hard navigation: unloads the SPA so no remaining component can fire an
      // authenticated request (or navigate to /error) with the dead session.
      window.location.replace("/");
    }
  };

  const dashboardCompanyUrl =
    companySelected && canUpdateCompany && validateId(companyId)
      ? `${APP_URLS.dashboardUrl}companies/${companyId}/general/info`
      : undefined;

  const partialUserData = {
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    category: user?.userCategory?.slug ?? "",
    isNewraiUser: user?.userCategory?.slug === "newrai",
  };

  return (
    <Layout>
      <Header
        appLogo={<Logo style={{ height: "30px" }} />}
        appName="NeoFact"
        userData={partialUserData}
        companySelected={companySelected}
        accountingFirmSelected={accountingFirmSelected}
        selectCompanyOptions={{
          isEditable: true,
          companyOnly: userIsClient,
          handleOnSelectCompany: (companyData: unknown) => {
            const company = companyData as CompanySelected;
            routes.navigate(`/company/${company.id}`);
          },
          handleOnSelectAccountingFirm: (accountingFirm: unknown) => {
            setAccountingFirmSelected(accountingFirm as AccountingFirm);
            routes.navigate("/");
          },
        }}
        userAvatar={{
          profileHref: `${APP_URLS.dashboardUrl}account/profile`,
          handleLogout: handleLogout,
        }}
        appsUrlParams={{
          companyId: companySelected?.id,
          accountingFirmId: accountingFirmSelected?.id,
        }}
        showNotifications
        dashboardCompanyUrl={dashboardCompanyUrl}
      />

      {props.children}
    </Layout>
  );
}

export default React.memo(ApplicationWrapper);
